import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Sparkles, ArrowRight, Check, X, Zap, Crown, Building } from "lucide-react";
import { useSubscription, isAdminEmail, type PlanType } from "@/lib/subscriptionContext";
import { useAuth } from "@/lib/authContext";

interface UpgradeModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  feature: string;
  requiredPlan?: string;
}

const PLANS = [
  { key: "free" as PlanType, name: "Free", monthly: 0, yearly: 0, icon: Zap },
  { key: "pro" as PlanType, name: "Pro", monthly: 99, yearly: 82, icon: Crown },
  { key: "business" as PlanType, name: "Studio", monthly: 179, yearly: 149, icon: Building },
];

type PlanCol = "free" | "pro" | "business";
const FEATURES: { label: string; values: Record<PlanCol, string | boolean> }[] = [
  { label: "Roadmaps", values: { free: "1", pro: "10", business: "25" } },
  { label: "PDF Export", values: { free: false, pro: true, business: true } },
  { label: "Financial Forecast", values: { free: false, pro: true, business: true } },
  { label: "AI Advisor", values: { free: "5 questions", pro: "Unlimited", business: "Unlimited" } },
  { label: "Operations Suite", values: { free: false, pro: true, business: true } },
  { label: "Staff Rota", values: { free: false, pro: true, business: true } },
  { label: "Survival Score", values: { free: false, pro: true, business: true } },
  { label: "Team Members", values: { free: "1", pro: "1", business: "5" } },
  { label: "White-label Exports", values: { free: false, pro: false, business: true } },
];

function CellVal({ value }: { value: string | boolean }) {
  if (typeof value === "boolean") {
    return value
      ? <Check className="w-3.5 h-3.5 text-primary mx-auto" />
      : <X className="w-3.5 h-3.5 text-muted-foreground/30 mx-auto" />;
  }
  return <span className="text-xs font-medium text-foreground">{value}</span>;
}

export default function UpgradeModal({ open, onOpenChange, feature, requiredPlan = "Pro" }: UpgradeModalProps) {
  const navigate = useNavigate();
  const { subscription } = useSubscription();
  const { user } = useAuth();
  const [annual, setAnnual] = useState(false);
  if (isAdminEmail(user?.email)) return null;
  const currentPlan = subscription?.plan || "free";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-2">
            <Sparkles className="w-6 h-6 text-primary" />
          </div>
          <DialogTitle className="font-heading text-xl">Unlock {feature}</DialogTitle>
          <DialogDescription>
            <span className="font-medium text-foreground">{feature}</span> is available on the {requiredPlan} plan and above.
            Compare what you get with each plan below.
          </DialogDescription>
        </DialogHeader>

        {/* Monthly / Yearly toggle */}
        <div className="flex items-center justify-center gap-2 mt-2">
          <span className={`text-xs font-medium ${!annual ? "text-foreground" : "text-muted-foreground"}`}>Monthly</span>
          <Switch checked={annual} onCheckedChange={setAnnual} />
          <span className={`text-xs font-medium ${annual ? "text-foreground" : "text-muted-foreground"}`}>Yearly</span>
          <span className="text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-emerald-500 text-white ml-1">
            Save 2 months
          </span>
        </div>

        {/* Compact comparison table */}
        <div className="mt-4 overflow-x-auto">
          <table className="w-full text-xs border-collapse">
            <thead>
              <tr>
                <th className="text-left py-2 px-2 w-[120px]" />
                {PLANS.map(p => {
                  const isCurrent = p.key === currentPlan;
                  const Icon = p.icon;
                  const price = annual ? p.yearly : p.monthly;
                  return (
                    <th key={p.key} className={`text-center py-2 px-2 ${isCurrent ? "bg-primary/5 rounded-t-lg" : ""}`}>
                      <Icon className={`w-4 h-4 mx-auto mb-1 ${isCurrent ? "text-primary" : "text-muted-foreground"}`} />
                      <div className="font-medium text-foreground">{p.name}</div>
                      {isCurrent && <Badge variant="default" className="text-[8px] px-1 py-0 mt-0.5">Current</Badge>}
                      <div className="text-muted-foreground mt-0.5">{price === 0 ? "Free" : `$${price}/mo`}</div>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {FEATURES.map(feat => (
                <tr key={feat.label} className="border-b border-border/30">
                  <td className="py-1.5 px-2 text-muted-foreground">{feat.label}</td>
                  {PLANS.map(p => (
                    <td key={p.key} className={`py-1.5 px-2 text-center ${p.key === currentPlan ? "bg-primary/5" : ""}`}>
                      <CellVal value={feat.values[p.key as PlanCol]} />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="flex flex-col gap-3 mt-4">
          <Button className="w-full gap-2" onClick={() => { onOpenChange(false); navigate("/pricing"); }}>
            See Full Plan Details <ArrowRight className="w-4 h-4" />
          </Button>
          <Button variant="ghost" className="w-full" onClick={() => onOpenChange(false)}>
            Not right now
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
